import LinkRoundedIcon from '@material-ui/icons/LinkRounded';
import { Form as FormikForm } from 'formik';
import React from 'react';
import styled, { css } from 'styled-components/macro';
import { iconButtonStyles } from '../../common/elements/Button';
import {
  focusRing,
  fontSize,
  fontWeight,
  layout,
  lineHeight,
  media,
  neutral,
  radius,
  shadow,
  space,
  transition,
} from '../../../theme/tokens';
import { DeleteAssessmentButton } from '../../common/elements/Assessments';
import { CreateButton } from '../../common/elements/Form';
import { StaffHeader } from '../../common/elements/Staff';
import * as ModuleCard from './ModuleCard';

export const ModuleInfoContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: space-between;
  gap: ${space[2]};
`;

export const ModuleInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${space[3]};
  min-width: 0;
`;

export const ModuleHeader = styled(StaffHeader)`
  line-height: ${lineHeight.tight};
  font-size: ${({ characterLength }) => {
    if (characterLength < 50) return fontSize.xxl;
    if (characterLength < 100) return fontSize.xl;
    else return fontSize.md;
  }};
`;

export const ModuleImage = styled(ModuleCard.ModuleImage)``;

export const AlignModuleButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space[2]};
`;

export const LockControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${space[1]};
  min-height: ${layout.touchTarget};
  padding: 0 ${space[1]} 0 ${space[3]};
  border: 1px solid ${neutral.border};
  border-radius: ${radius.pill};
  background: ${neutral.surface};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.bold};
  color: ${({ locked, theme }) =>
    locked ? theme.headerColour : neutral.textMuted};
`;

export const SharedChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${space[1]};
  min-height: 2.8rem;
  padding: 0 ${space[3]};
  border-radius: ${radius.pill};
  background: ${neutral.subtle};
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.bold};
  color: ${({ theme }) => theme.headerColour};
`;

export const ModuleCreatedBy = styled.p`
  margin: ${space[1]} 0 0;
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.semibold};
  color: ${neutral.textMuted};
`;

export const EditModuleButton = styled(CreateButton)`
  align-self: center;
  margin: 0;
`;

export const ModuleNav = styled.ol`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space[1]};
  margin: 0;
  list-style: none;
  padding-left: 0;
  align-self: center;
`;

export const ModuleListItem = styled.li`
  display: inline;
`;

export const AddFirstItemButton = styled(EditModuleButton)`
  justify-self: center;
  padding: ${space[2]} ${space[6]};
  font-size: ${fontSize.xl};
`;

export const StaffModuleForm = styled(FormikForm)`
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: ${space[2]};
`;

export const Label = styled.label`
  margin-top: ${space[2]};
  color: ${({ theme }) => theme.headerColour};
  font-style: normal;
  font-weight: ${fontWeight.bold};
  font-size: ${fontSize.md};
  line-height: ${lineHeight.tight};
`;

const fieldStyles = css`
  width: 100%;
  min-height: ${layout.touchTarget};
  margin: 0 0 ${space[2]};
  padding: ${space[2]} ${space[3]};
  border: 1px solid ${neutral.border};
  border-radius: ${radius.md};
  font-family: inherit;
  font-size: ${fontSize.md};
  line-height: ${lineHeight.normal};
  color: ${({ theme }) => theme.headerColour};
  background: ${({ theme }) => theme.appBackground};
  transition: border-color ${transition.fast};
  &:hover:not(:disabled) {
    border-color: ${neutral.borderStrong};
  }
  &:focus {
    border-color: ${({ theme }) => theme.buttonPrimary};
  }
  &:disabled {
    background: ${({ theme }) => theme.appBackground};
    color: ${neutral.textMuted};
  }
  ${focusRing}
`;

const StyledTextInput = styled.input`
  ${fieldStyles}
`;

export const TextInput = ({ field, form, ...props }) => (
  <StyledTextInput {...field} {...props} />
);

const StyledTextArea = styled.textarea`
  ${fieldStyles}
  height: 16rem;
  resize: vertical;
`;

export const TextArea = ({ field, form, ...props }) => {
  return <StyledTextArea {...field} {...props} />;
};

export const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.appBackground};
  border: 1px solid ${neutral.border};
  border-radius: ${radius.lg};
  padding: ${space[3]};

  ${media.md`
    flex-grow: 1;
    height: 100%;
    padding: ${space[4]};
  `}
`;

export const EmbedPreview = styled.iframe`
  /* phones: a 16:9 frame rather than stretching to fill the page */
  flex-grow: 0;
  width: 100%;
  aspect-ratio: 16 / 9;
  border: none;
  border-radius: ${radius.md};
  box-shadow: ${shadow.lg};
  padding-bottom: 1px;

  ${media.md`
    flex-grow: 0.95;
    width: 95%;
    aspect-ratio: auto;
  `}
`;

export const CopyLinkButton = styled.button`
  ${iconButtonStyles}
`;

export const LinkIcon = () => (
  <LinkRoundedIcon style={{ fontSize: fontSize.xxl }} />
);

export const LinkSaveContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: ${space[2]};
`;

export const DeleteItemButton = styled(DeleteAssessmentButton)`
  margin: 0;
`;

export * from '../../auth/elements/StaffAuth';
export * from '../../common/elements/Form';
export * from '../../common/elements/Staff';
